<template>
  <DialogHeader>
    <template #right>
      <div class="font-medium text-green-900 cursor-pointer" @click="close()">
        Done
      </div>
    </template>
    {{ props.matches?.breakdown.length }} potential infringement matches
  </DialogHeader>
  <DialogContent>
    <div
      class="rounded-lg px-5 py-[14px] flex items-center justify-between mb-[14px]"
      :class="overallBgClass"
    >
      <div
        class="max-w-[178px] text-[14px] leading-[20px] font-medium font-primary text-white"
      >
        Overall potential infringement match
      </div>

      <div class="flex items-center">
        <div
          class="mr-2.5 font-secondary font-medium text-[16px] leading-[18px] text-white"
        >
          {{ props.matches?.score }}%
        </div>
        <div class="w-[200px] h-2 bg-white rounded-md">
          <div
            class="h-full rounded-md border"
            :class="overallBarClass"
            :style="{ width: `${props.matches?.score}%` }"
          ></div>
        </div>
      </div>
    </div>

    <div class="font-secondary text-[14px] leading-[22px] text-gray-800">
      Preview of potential infringement matches from report
    </div>

    <Link class="mb-[14px]">Get report</Link>

    <div class="overflow-auto">
      <div
        v-for="(group, type) in groupedBreakdown"
        :key="type"
        class="mb-[14px] last:mb-0"
      >
        <div
          class="h-[41px] flex items-center font-secondary font-medium text-[14px] leading-[18px] text-gray-800 border-b border-gray-800/8"
        >
          {{ categoryLabels[type as MatchCategoryType] }}
        </div>

        <div
          v-for="(item, index) in group.slice(0, MAX_VISIBLE_PER_TYPE)"
          :key="index"
          class="h-[39px] flex items-center justify-between border-b border-gray-800/8"
        >
          <div class="flex items-center gap-x-2.5">
            <Icon
              :name="categoryIcons[type as MatchCategoryType]"
              :size="16"
              class="stroke-gray-800"
            />
            <div
              class="font-secondary text-[14px] leading-[18px] text-gray-800"
            >
              {{ categoryLabels[type as MatchCategoryType] }} match
              {{ index + 1 }}
            </div>
          </div>

          <div class="flex items-center">
            <div
              class="mr-1.5 font-secondary font-medium text-[14px] leading-[18px]"
              :class="scoreTextClass(item.level)"
            >
              {{ item.score }}%
            </div>
            <div class="w-[100px] h-2 bg-gray-200/50 rounded-md">
              <div
                class="h-full rounded-md"
                :class="scoreBgClass(item.level)"
                :style="{ width: `${item.score}%` }"
              ></div>
            </div>
          </div>
        </div>

        <div
          v-if="group.length > MAX_VISIBLE_PER_TYPE"
          class="font-secondary text-[14px] leading-[18px] text-gray-500 py-2.5"
        >
          + {{ group.length - MAX_VISIBLE_PER_TYPE }} more
        </div>
      </div>
    </div>
  </DialogContent>
  <DialogFooter>
    <UiButton class="w-1/2 tablet-h:w-auto" primary @click="close()">
      Done
    </UiButton>
  </DialogFooter>
</template>

<script setup lang="ts">
import DialogHeader from '@/shared/dialogs/DialogHeader.vue'
import DialogContent from '@/shared/dialogs/DialogContent.vue'
import DialogFooter from '@/shared/dialogs/DialogFooter.vue'
import { useDialog } from '@/shared/dialogs/useDialog'
import {
  MatchCategoryType,
  ScoreLevel,
  type InfringementListQuery,
} from '@/shared/graphql/router/graphql'
import { Icon, UiButton, type IconName } from '@dacr/ui'
import { computed } from 'vue'
import Link from '@/shared/components/Link.vue'

type MatchSummary =
  InfringementListQuery['infringements']['edges'][number]['node']['matches']

type BreakdownItem = NonNullable<MatchSummary>['breakdown'][number]

const props = defineProps<{
  matches?: MatchSummary | null
}>()

const { close } = useDialog()

const MAX_VISIBLE_PER_TYPE = 5

const groupedBreakdown = computed(() => {
  const groups: Partial<Record<MatchCategoryType, BreakdownItem[]>> = {}

  for (const item of props.matches?.breakdown ?? []) {
    if (!groups[item.type]) {
      groups[item.type] = []
    }
    groups[item.type]!.push(item)
  }

  return groups
})

const overallBgClass = {
  'bg-green-500': props.matches?.level === ScoreLevel.Low,
  'bg-yellow-500': props.matches?.level === ScoreLevel.Medium,
  'bg-red-500': props.matches?.level === ScoreLevel.High,
}

const overallBarClass = {
  'bg-green-600 border-white': props.matches?.level === ScoreLevel.Low,
  'bg-yellow-600 border-white': props.matches?.level === ScoreLevel.Medium,
  'bg-red-500 border-white': props.matches?.level === ScoreLevel.High,
}

const categoryLabels: Record<MatchCategoryType, string> = {
  [MatchCategoryType.Audio]: 'Audio',
  [MatchCategoryType.Image]: 'Visual',
  [MatchCategoryType.Text]: 'Text',
  [MatchCategoryType.Transcript]: 'Transcript',
  [MatchCategoryType.Video]: 'Video',
}

const categoryIcons: Record<MatchCategoryType, IconName> = {
  [MatchCategoryType.Audio]: 'Audio',
  [MatchCategoryType.Image]: 'Image',
  [MatchCategoryType.Text]: 'FileText',
  [MatchCategoryType.Transcript]: 'Text',
  [MatchCategoryType.Video]: 'Play',
}

const scoreTextClass = (level: ScoreLevel) => ({
  'text-green-500': level === ScoreLevel.Low,
  'text-yellow-500': level === ScoreLevel.Medium,
  'text-red-500': level === ScoreLevel.High,
})

const scoreBgClass = (level: ScoreLevel) => ({
  'bg-green-500': level === ScoreLevel.Low,
  'bg-yellow-500': level === ScoreLevel.Medium,
  'bg-red-500': level === ScoreLevel.High,
})
</script>
